import type { ChannelCheckShiftRun } from '@app/model/channel_check/ChannelCheckModels';

export type ChannelCheckAlignment = { target: number, rank: number, priceChange: number };
export type ChannelCheckImpactRun = { run: ChannelCheckShiftRun, alignment: ChannelCheckAlignment | null, impact: number };

export const channelCheckAlignment = (run: Pick<ChannelCheckShiftRun, 'chPrice' | 'refRank' | 'chLadder'>): ChannelCheckAlignment | null => {
	const prices = run.chLadder.filter(hotel => hotel.inLadder && !hotel.isMain).map(hotel => hotel.price);
	if (!Number.isFinite(run.chPrice) || run.chPrice < 0 || !Number.isInteger(run.refRank)
		|| run.refRank < 1 || run.refRank > prices.length + 1 || prices.some(price => !Number.isFinite(price) || price < 0)) return null;
	prices.sort((a, b) => a - b);
	// Integer prices only change rank just above a competitor's raw price.
	// Scan these intervals, never every euro between prices. Equal-price ranks may be skipped.
	const boundaries = [0, ...new Set(prices.map(price => Math.floor(price) + 1))];
	let cheaperCount = 0;
	let best: ChannelCheckAlignment | null = null;
	for (let index = 0; index < boundaries.length; index++) {
		const lower = boundaries[index];
		const upper = index + 1 < boundaries.length ? boundaries[index + 1] - 1 : Infinity;
		while (cheaperCount < prices.length && prices[cheaperCount] < lower) cheaperCount++;
		const rank = cheaperCount + 1;
		const target = Math.max(lower, Math.min(upper, Math.round(run.chPrice)));
		const candidate = { target, rank, priceChange: Math.abs(target - run.chPrice) };
		if (best === null || Math.abs(rank - run.refRank) < Math.abs(best.rank - run.refRank)
			|| (Math.abs(rank - run.refRank) === Math.abs(best.rank - run.refRank) && rank < best.rank)) best = candidate;
	}
	return best;
};

export const orderChannelCheckRuns = (runs: ChannelCheckShiftRun[]): ChannelCheckImpactRun[] => runs.map(run => {
	const alignment = channelCheckAlignment(run);
	return { run, alignment, impact: alignment === null ? -1 : alignment.priceChange * run.nights };
}).sort((a, b) => {
	const suspectOrder = Number(a.run.keyMover?.isSuspect === true) - Number(b.run.keyMover?.isSuspect === true);
	if (suspectOrder !== 0) return suspectOrder;
	const placesOrder = Math.abs(b.run.chRank - b.run.refRank) - Math.abs(a.run.chRank - a.run.refRank);
	if (placesOrder !== 0) return placesOrder;
	const impactOrder = b.impact - a.impact;
	return impactOrder !== 0 ? impactOrder : a.run.firstNight.localeCompare(b.run.firstNight);
});

export const formatChannelCheckPrice = (value: number) => `€${value.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(value) ? 0 : 2, maximumFractionDigits: 2 })}`;
